/**
 * hldExport.ts — Client-side Export_Service for the HLD Workspace (Req 15).
 *
 * Generates a single, self-contained, downloadable file IN THE BROWSER
 * (Blob + object URL) representing a completed Workspace_Session. Nothing is
 * ever stored on a server (Req 15.1).
 *
 * The module is split into two layers so the composition logic is verifiable
 * in isolation (task 13.2):
 *   1. A PURE content-generation layer (`buildExportContent`,
 *      `generateExportMarkdown`, `isSessionEmpty`, section builders) that turns
 *      a `WorkspaceSession` into a Markdown string with no DOM/browser access.
 *   2. A thin DOM layer (`triggerDownload`, `exportSession`) that wraps the
 *      pure output in a Blob, creates an object URL, and clicks a temporary
 *      anchor to download it.
 *
 * Format: Markdown. The diagram is rendered both as a human-readable
 * node/edge list AND as its canonical JSON (Req 15.1 "diagram section").
 *
 * Requirements: 15.1, 15.2, 15.3, 15.4, 15.5.
 */

import { WorkspaceSession, DiagramState } from '../types/hld';
import { serialize } from './diagramState';

// ---------------------------------------------------------------------------
// Types and constants
// ---------------------------------------------------------------------------

/**
 * Thrown by the pure content generator when every one of the five sections
 * (requirements, estimations, apiDesign, dataModel, diagram) is empty (Req
 * 15.5). Callers should catch this and present a "nothing to export" message,
 * leaving the Workspace_Session unchanged.
 */
export class NothingToExportError extends Error {
 /** Machine-readable code for callers that prefer branching over `instanceof`. */
 readonly code = 'NOTHING_TO_EXPORT';

 constructor(message = 'Nothing to export: all sections are empty.') {
  super(message);
  this.name = 'NothingToExportError';
  // Restore prototype chain for reliable `instanceof` across transpile targets.
  Object.setPrototypeOf(this, NothingToExportError.prototype);
 }
}

/** The explicit marker inserted for a section that has no saved content (Req 15.3). */
export const EMPTY_SECTION_MARKER = '_(empty — no content saved)_';

/** Options for the pure content generator. */
export interface ExportContentOptions {
 /** Human-readable session title for the header (Req 15.2). */
 problemTitle?: string;
 /**
  * Export creation timestamp (Req 15.2). Injectable for deterministic tests;
  * defaults to `new Date()` when omitted.
  */
 now?: Date;
}

/** The result of building an export: everything the DOM layer needs. */
export interface ExportResult {
 /** Suggested download filename (e.g. `hld-url-shortener-2025-01-15.md`). */
 filename: string;
 /** The full Markdown document. */
 content: string;
 /** MIME type for the Blob. */
 mimeType: string;
}

// ---------------------------------------------------------------------------
// Emptiness checks (Req 15.3, 15.5)
// ---------------------------------------------------------------------------

function isBlank(value: string | undefined | null): boolean {
 return value == null || value.trim().length === 0;
}

function isRequirementsEmpty(session: WorkspaceSession): boolean {
 const req = session.requirements;
 if (!req) return true;
 const functional = (req.functional ?? []).filter((e) => !isBlank(e));
 const nonFunctional = (req.nonFunctional ?? []).filter((e) => !isBlank(e));
 return functional.length === 0 && nonFunctional.length === 0;
}

function isEstimationsEmpty(session: WorkspaceSession): boolean {
 const est = session.estimations;
 if (!est) return true;
 const values = [est.dau, est.qps, est.storage, est.bandwidth, est.cacheSize];
 // A field counts as "saved content" only when it is a real, finite number.
 return !values.some((v) => typeof v === 'number' && Number.isFinite(v));
}

function isApiDesignEmpty(session: WorkspaceSession): boolean {
 return isBlank(session.apiDesign);
}

function isDataModelEmpty(session: WorkspaceSession): boolean {
 return isBlank(session.dataModel);
}

function isDiagramEmpty(session: WorkspaceSession): boolean {
 const diagram = session.diagramJson;
 if (!diagram) return true;
 const nodes = diagram.nodes ?? [];
 const edges = diagram.edges ?? [];
 return nodes.length === 0 && edges.length === 0;
}

/**
 * True when ALL five sections are empty (Req 15.5). When this holds, the export
 * is rejected via `NothingToExportError`.
 */
export function isSessionEmpty(session: WorkspaceSession): boolean {
 return (
  isRequirementsEmpty(session) &&
  isEstimationsEmpty(session) &&
  isApiDesignEmpty(session) &&
  isDataModelEmpty(session) &&
  isDiagramEmpty(session)
 );
}

// ---------------------------------------------------------------------------
// Section builders (pure) — each ALWAYS emits its section, using the empty
// marker when there is no saved content (Req 15.3).
// ---------------------------------------------------------------------------

function buildRequirementsSection(session: WorkspaceSession): string {
 const lines: string[] = ['## Requirements', ''];
 if (isRequirementsEmpty(session)) {
  lines.push(EMPTY_SECTION_MARKER);
  return lines.join('\n');
 }
 const functional = (session.requirements?.functional ?? []).filter((e) => !isBlank(e));
 const nonFunctional = (session.requirements?.nonFunctional ?? []).filter((e) => !isBlank(e));

 lines.push('### Functional');
 if (functional.length === 0) {
  lines.push(EMPTY_SECTION_MARKER);
 } else {
  functional.forEach((entry) => lines.push(`- ${entry.trim()}`));
 }
 lines.push('');
 lines.push('### Non-functional');
 if (nonFunctional.length === 0) {
  lines.push(EMPTY_SECTION_MARKER);
 } else {
  nonFunctional.forEach((entry) => lines.push(`- ${entry.trim()}`));
 }
 return lines.join('\n');
}

function buildEstimationsSection(session: WorkspaceSession): string {
 const lines: string[] = ['## Estimations', ''];
 if (isEstimationsEmpty(session)) {
  lines.push(EMPTY_SECTION_MARKER);
  return lines.join('\n');
 }
 const est = session.estimations ?? {};
 const rows: Array<[string, number | undefined]> = [
  ['Daily Active Users (DAU)', est.dau],
  ['Queries Per Second (QPS)', est.qps],
  ['Storage', est.storage],
  ['Bandwidth', est.bandwidth],
  ['Cache Size', est.cacheSize],
 ];
 lines.push('| Metric | Value |');
 lines.push('| --- | --- |');
 rows.forEach(([label, value]) => {
  const rendered =
   typeof value === 'number' && Number.isFinite(value) ? String(value) : '—';
  lines.push(`| ${label} | ${rendered} |`);
 });
 return lines.join('\n');
}

function buildApiDesignSection(session: WorkspaceSession): string {
 const lines: string[] = ['## API Design', ''];
 if (isApiDesignEmpty(session)) {
  lines.push(EMPTY_SECTION_MARKER);
  return lines.join('\n');
 }
 lines.push('```');
 lines.push(session.apiDesign);
 lines.push('```');
 return lines.join('\n');
}

function buildDataModelSection(session: WorkspaceSession): string {
 const lines: string[] = ['## Data Model', ''];
 if (isDataModelEmpty(session)) {
  lines.push(EMPTY_SECTION_MARKER);
  return lines.join('\n');
 }
 lines.push('```');
 lines.push(session.dataModel);
 lines.push('```');
 return lines.join('\n');
}

function buildDiagramSection(session: WorkspaceSession): string {
 const lines: string[] = ['## Diagram', ''];
 if (isDiagramEmpty(session)) {
  lines.push(EMPTY_SECTION_MARKER);
  return lines.join('\n');
 }
 // Serialize through the pure diagram core so the exported JSON matches the
 // canonical round-trip shape (Req 11.1). Fall back to the raw shape if the
 // session carries a non-standard object.
 let diagram: DiagramState;
 try {
  diagram = serialize(session.diagramJson);
 } catch {
  diagram = session.diagramJson;
 }
 const nodes = diagram.nodes ?? [];
 const edges = diagram.edges ?? [];

 lines.push('### Nodes');
 if (nodes.length === 0) {
  lines.push(EMPTY_SECTION_MARKER);
 } else {
  nodes.forEach((n) =>
   lines.push(`- **${n.label}** (${n.type}) — id: \`${n.id}\` @ (${n.position.x}, ${n.position.y})`)
  );
 }
 lines.push('');
 lines.push('### Connections');
 if (edges.length === 0) {
  lines.push(EMPTY_SECTION_MARKER);
 } else {
  edges.forEach((e) => {
   const proto = e.protocol ? ` [${e.protocol}]` : '';
   const label = e.label ? ` "${e.label}"` : '';
   lines.push(`- \`${e.source}\` → \`${e.target}\`${proto}${label} — id: \`${e.id}\``);
  });
 }
 lines.push('');
 lines.push('### Diagram JSON');
 lines.push('```json');
 lines.push(JSON.stringify(diagram, null, 2));
 lines.push('```');
 return lines.join('\n');
}

// ---------------------------------------------------------------------------
// Header + top-level document assembly (pure)
// ---------------------------------------------------------------------------

function formatDate(date: Date): string {
 // ISO date + time, human-readable and locale-independent for stable output.
 return date.toISOString();
}

function buildHeaderSection(title: string, now: Date): string {
 return [
  `# HLD Solution — ${title}`,
  '',
  `- **Session title:** ${title}`,
  `- **Export created:** ${formatDate(now)}`,
 ].join('\n');
}

/**
 * PURE. Build the full Markdown export document for a session (Req 15.1–15.3).
 *
 * Includes a header (title + export date), then the five sections in a fixed
 * order, each ALWAYS present (empty ones carry the explicit empty marker).
 *
 * @throws {NothingToExportError} when all five sections are empty (Req 15.5).
 */
export function generateExportMarkdown(
 session: WorkspaceSession,
 options: ExportContentOptions = {}
): string {
 if (isSessionEmpty(session)) {
  throw new NothingToExportError();
 }
 const now = options.now ?? new Date();
 const title = (options.problemTitle ?? session.problemId ?? 'Untitled').trim() || 'Untitled';

 const sections = [
  buildHeaderSection(title, now),
  buildRequirementsSection(session),
  buildEstimationsSection(session),
  buildApiDesignSection(session),
  buildDataModelSection(session),
  buildDiagramSection(session),
 ];

 // Blank line between each top-level block, trailing newline at EOF.
 return sections.join('\n\n') + '\n';
}

function slugify(value: string): string {
 return (
  value
   .toLowerCase()
   .replace(/[^a-z0-9]+/g, '-')
   .replace(/^-+|-+$/g, '')
   .slice(0, 60) || 'session'
 );
}

/**
 * PURE. Build a complete `ExportResult` (filename + content + mimeType) for a
 * session. This is the single composition point the DOM layer and tests share.
 *
 * @throws {NothingToExportError} when all five sections are empty (Req 15.5).
 */
export function buildExportContent(
 session: WorkspaceSession,
 problemTitle?: string,
 options: Omit<ExportContentOptions, 'problemTitle'> = {}
): ExportResult {
 const now = options.now ?? new Date();
 const content = generateExportMarkdown(session, { problemTitle, now });
 const title = (problemTitle ?? session.problemId ?? 'session').trim() || 'session';
 const datePart = formatDate(now).slice(0, 10); // YYYY-MM-DD
 const filename = `hld-${slugify(title)}-${datePart}.md`;
 return { filename, content, mimeType: 'text/markdown;charset=utf-8' };
}

// ---------------------------------------------------------------------------
// DOM layer — Blob + object URL download (Req 15.1). Kept intentionally thin
// and separate from the pure content generation above.
// ---------------------------------------------------------------------------

/**
 * Trigger a browser download for an already-built `ExportResult` using a Blob
 * and a temporary object URL. No server round-trip; nothing is persisted
 * remotely (Req 15.1).
 */
export function triggerDownload(result: ExportResult): void {
 if (typeof document === 'undefined' || typeof URL === 'undefined' || typeof Blob === 'undefined') {
  throw new Error('Download is only available in a browser environment.');
 }
 const blob = new Blob([result.content], { type: result.mimeType });
 const url = URL.createObjectURL(blob);
 try {
  const anchor = document.createElement('a');
  anchor.href = url;
  anchor.download = result.filename;
  anchor.style.display = 'none';
  document.body.appendChild(anchor);
  anchor.click();
  document.body.removeChild(anchor);
 } finally {
  // Revoke on the next tick so the click has a chance to start the download.
  setTimeout(() => URL.revokeObjectURL(url), 0);
 }
}

/**
 * Top-level export entry point: build the file content (pure) and trigger the
 * browser download (DOM). Returns the `ExportResult` for callers/tests that
 * want to inspect what was produced.
 *
 * @throws {NothingToExportError} when all five sections are empty (Req 15.5),
 *         in which case NO download is triggered and the session is untouched.
 */
export function exportSession(
 session: WorkspaceSession,
 problemTitle?: string,
 options: Omit<ExportContentOptions, 'problemTitle'> = {}
): ExportResult {
 const result = buildExportContent(session, problemTitle, options);
 triggerDownload(result);
 return result;
}
